import { ColumnWidths } from '@/components/layout/FormLayout';
import { CurrencyUSD } from '@/graphql/scalars';
import { InsurancePolicyDetails } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { EntityType } from '@/modules/entities/types/EntityType';
import { LiabilityFormShape } from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import {
  ClientOrganizationKind,
  ClientProfileRelationshipType,
  EntityInEstateStatus,
  TestamentaryEntityKind,
} from '@/types/schema';

export interface AIOnboardingModalFormShape {
  individuals: AIOnboardingModalIndividualField[];
  organizations: AIOnboardingModalOrganizationField[];
  entities: AIOnboardingModalEntityField[];
  testamentaryEntities: AIOnboardingModalTestamentaryEntityField[];
  assets: AIOnboardingAssetField[];
  liabilities: AIOnboardingLiabilityField[];
  lifeInsurancePolicies: AIOnboardingLifeInsurancePolicyField[];
}

export type AIOnboardingModalFormShapeKeys = keyof AIOnboardingModalFormShape;

export type AIOnboardingModalFormFieldKeys<
  K extends AIOnboardingModalFormShapeKeys,
> = Exclude<
  keyof AIOnboardingModalFormShape[K][number],
  '_suggestionID' | '_showBasicAssetFields' | '_dateOfBirth'
>;

export type AIOnboardingModalFormFieldConfiguration = {
  [K in AIOnboardingModalFormShapeKeys]: {
    columns: Record<
      AIOnboardingModalFormFieldKeys<K>,
      {
        columnHeader: string;
        columnSubheader?: string;
        columnWidth: ColumnWidths;
        columnHeaderTooltip?: string;
      }
    >;
  } & {
    styles: { rowPaddingVertical: number; columnPaddingHorizontal: number };
  };
};

export interface AIOnboardingField {
  /**
   * _suggestionID is not a real form field. It's the id of the AISuggestion.
   * This will be undefined if the row got added manually by the user (not from a suggestion).
   */
  _suggestionID: string | undefined;
}

export const AIOnboardingIndividual_SELF = 'SELF' as const;

export interface AIOnboardingModalIndividualField extends AIOnboardingField {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  _dateOfBirth: Date | null;
  relationshipToPrimaryClient1:
    | ClientProfileRelationshipType
    | ''
    | typeof AIOnboardingIndividual_SELF;
  relationshipToPrimaryClient2:
    | ClientProfileRelationshipType
    | ''
    | typeof AIOnboardingIndividual_SELF;
}

export interface AIOnboardingModalOrganizationField extends AIOnboardingField {
  organizationName: string;
  organizationKind: ClientOrganizationKind | '';
}

export interface AIOnboardingModalEntityField extends AIOnboardingField {
  entityName: string;
  entityType: EntityType | '';
  principalIds: string[]; // Use this field to handle multi-select principals for supported entity types
  principalId: string; // Use this field to handle single-select principals for supported entity types
  integrationEntityIds: string[];
  _showBasicAssetFields?: boolean; // If true, show the below fields for the entity row
  assetCategoryId: string;
  currentMarketValue: CurrencyUSD | null;
  dateOfValuation: Date | null;
}

export interface AIOnboardingModalTestamentaryEntityField
  extends AIOnboardingField {
  testamentaryEntityName: string;
  testamentaryEntityKind: TestamentaryEntityKind | '';
  testamentaryEntityAssetLocation: EntityInEstateStatus | '';
}

// Assets (HOLDINGS)
export interface AIOnboardingAssetField extends AIOnboardingField {
  assetName: string;
  ownerId: string; // entity or client profile pulid
  assetCategoryId: string;
  valuation: CurrencyUSD | null;
}

export interface AIOnboardingLiabilityField
  extends AIOnboardingField,
    LiabilityFormShape {}

export interface AIOnboardingLifeInsurancePolicyField
  extends AIOnboardingField,
    InsurancePolicyDetails {
  // Additional field for tracking which entity owns this policy
  ownerId: string;
}
